import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../../shared/api';
import { Btn, Card, DataTable, Pill, Select, type Col } from '../../shared/ui';
import {
    Check, Field, FormGrid, Input, Note, Section, STATUS_LABEL, day, tone, yes,
    type Intermediary, type Props,
} from './posrShared';

/** m.1.2 Registar posrednika: popis, upis kandidata, uvjeti iz poglavlja 8, odobrenja i status. */

type Form = Record<string, string | boolean>;

const EMPTY: Form = {
    name: '', oib: '', kind: 'doo', vat_registered: true, beneficial_owner: '', contact_person: '', email: '', phone: '', iban: '',
    contract_date: '', termination_date: '', notice_end_date: '', kyc_status: 'ceka', kyc_checked_at: '',
    sanctions_pep_ok: false, criminal_record_ok: false, conflict_of_interest_ok: false, conflict_exception_approved: false,
    code_of_conduct_signed_at: '', training_completed_at: '', note: '',
};

function toForm(r: Intermediary): Form {
    const f: Form = { ...EMPTY };
    for (const k of Object.keys(EMPTY)) {
        const v = (r as unknown as Record<string, unknown>)[k];
        f[k] = typeof EMPTY[k] === 'boolean' ? yes(v as number) : v === null || v === undefined ? '' : String(v).slice(0, k.endsWith('_at') || k.endsWith('_date') ? 10 : undefined);
    }
    return f;
}

function payload(f: Form): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(f)) out[k] = v === '' ? null : v;
    return out;
}

export function PosrIntermediaries({ token, isAdmin, onError }: Props) {
    const [rows, setRows] = useState<Intermediary[] | null>(null);
    const [filter, setFilter] = useState('');
    const [editing, setEditing] = useState<Intermediary | 'new' | null>(null);
    const [form, setForm] = useState<Form>(EMPTY);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        setRows(null);
        api<{ rows: Intermediary[] }>(`/posrednici/intermediaries${filter ? `?status=${filter}` : ''}`, token)
            .then((d) => setRows(d.rows)).catch((e: Error) => { onError(e.message); setRows([]); });
    }, [token, filter, onError]);
    useEffect(load, [load]);

    const set = (k: string) => (v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));
    const open = (r: Intermediary | 'new') => { setEditing(r); setForm(r === 'new' ? EMPTY : toForm(r)); };

    const run = async (fn: () => Promise<unknown>) => {
        setBusy(true);
        try { await fn(); load(); } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
    };

    const save = () => run(async () => {
        if (editing === 'new') await mutate('/posrednici/intermediaries', token, 'POST', payload(form));
        else if (editing) await mutate(`/posrednici/intermediaries/${editing.id}`, token, 'PATCH', payload(form));
        setEditing(null);
    });

    const act = (id: number, path: string, body: Record<string, unknown>) =>
        run(async () => { await mutate(`/posrednici/intermediaries/${id}/${path}`, token, 'POST', body); setEditing(null); });

    const cols: Col<Intermediary>[] = [
        { key: 'name', label: 'Posrednik', render: (r) => <div><div style={{ fontWeight: 700 }}>{r.name}</div><div style={{ fontSize: '0.75rem', color: '#8a90a3' }}>{r.kind} {r.oib ? `· OIB ${r.oib}` : ''}</div></div> },
        { key: 'status', label: 'Status', render: (r) => <Pill tone={tone(r.status)}>{STATUS_LABEL[r.status] ?? r.status}</Pill> },
        { key: 'contract_date', label: 'Ugovor', render: (r) => day(r.contract_date) },
        { key: 'probation_until', label: 'Probni do', render: (r) => day(r.probation_until) },
        { key: 'missing', label: 'Uvjeti', render: (r) => r.missing.length === 0 ? <Pill tone="ok">ispunjeni</Pill> : <Pill tone="warn">nedostaje {r.missing.length}</Pill> },
        { key: 'leads', label: 'Prijave', align: 'right' },
        { key: 'clients', label: 'Kupci', align: 'right' },
    ];

    const current = editing && editing !== 'new' ? rows?.find((r) => r.id === editing.id) ?? editing : null;

    return (
        <>
            <Card>
                <Section title="Registar posrednika" actions={<>
                    <Select value={filter} onChange={setFilter}>
                        <option value="">Svi statusi</option>
                        {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </Select>
                    <Btn variant="primary" onClick={() => open('new')}>Novi posrednik</Btn>
                </>}>
                    <DataTable cols={cols} rows={rows} onRowClick={(r) => open(r)} empty="Nema upisanih posrednika." />
                </Section>
            </Card>

            {editing ? (
                <Card>
                    <Section title={editing === 'new' ? 'Novi posrednik (kandidat)' : `Posrednik ${current?.name ?? ''}`} actions={<>
                        <Btn onClick={() => setEditing(null)}>Zatvori</Btn>
                        <Btn variant="primary" disabled={busy || !form.name} onClick={save}>Spremi</Btn>
                    </>}>
                        <FormGrid>
                            <Field label="Naziv"><Input value={String(form.name)} onChange={set('name')} /></Field>
                            <Field label="OIB"><Input value={String(form.oib)} onChange={set('oib')} /></Field>
                            <Field label="Vrsta">
                                <Select value={String(form.kind)} onChange={set('kind')} width="100%">
                                    <option value="doo">Društvo</option><option value="obrt">Obrt</option><option value="fizicka">Fizička osoba (iznimno)</option>
                                </Select>
                            </Field>
                            <Field label="Stvarni vlasnik"><Input value={String(form.beneficial_owner)} onChange={set('beneficial_owner')} /></Field>
                            <Field label="Kontakt osoba"><Input value={String(form.contact_person)} onChange={set('contact_person')} /></Field>
                            <Field label="E-mail"><Input value={String(form.email)} onChange={set('email')} /></Field>
                            <Field label="Telefon"><Input value={String(form.phone)} onChange={set('phone')} /></Field>
                            <Field label="IBAN"><Input value={String(form.iban)} onChange={set('iban')} /></Field>
                            <Field label="Datum ugovora"><Input type="date" value={String(form.contract_date)} onChange={set('contract_date')} /></Field>
                            <Field label="Datum otkaza"><Input type="date" value={String(form.termination_date)} onChange={set('termination_date')} /></Field>
                            <Field label="Istek otkaznog roka"><Input type="date" value={String(form.notice_end_date)} onChange={set('notice_end_date')} /></Field>
                            <Field label="Dubinska analiza (KYC)">
                                <Select value={String(form.kyc_status)} onChange={set('kyc_status')} width="100%">
                                    <option value="ceka">Čeka</option><option value="u_tijeku">U tijeku</option><option value="odobren">Odobren</option><option value="odbijen">Odbijen</option>
                                </Select>
                            </Field>
                            <Field label="KYC proveden"><Input type="date" value={String(form.kyc_checked_at)} onChange={set('kyc_checked_at')} /></Field>
                            <Field label="Kodeks potpisan"><Input type="date" value={String(form.code_of_conduct_signed_at)} onChange={set('code_of_conduct_signed_at')} /></Field>
                            <Field label="Obuka završena"><Input type="date" value={String(form.training_completed_at)} onChange={set('training_completed_at')} /></Field>
                        </FormGrid>
                        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                            <Check label="U sustavu PDV-a" checked={Boolean(form.vat_registered)} onChange={set('vat_registered')} />
                            <Check label="Sankcijski popisi i PEP provjereni" checked={Boolean(form.sanctions_pep_ok)} onChange={set('sanctions_pep_ok')} />
                            <Check label="Uvjerenje o nekažnjavanju" checked={Boolean(form.criminal_record_ok)} onChange={set('criminal_record_ok')} />
                            <Check label="Nije zaposlenik ni povezana osoba" checked={Boolean(form.conflict_of_interest_ok)} onChange={set('conflict_of_interest_ok')} />
                            <Check label="Iznimka od sukoba interesa odobrena (uprava)" checked={Boolean(form.conflict_exception_approved)} onChange={set('conflict_exception_approved')} disabled={!isAdmin} />
                        </div>
                        <Field label="Napomena" wide><Input value={String(form.note)} onChange={set('note')} /></Field>

                        {current ? (
                            <>
                                {current.missing.length > 0 ? (
                                    <Note>Prije statusa probni ili aktivan nedostaje sljedeće. {current.missing.join(' ')}</Note>
                                ) : <Note>Svi uvjeti su ispunjeni.</Note>}
                                <Note>
                                    Odobrenje uprave {current.approved_by_name ? `dao je ${current.approved_by_name}` : 'nije dano'}, a potvrdu za sprječavanje pranja novca {current.aml_confirmed_by_name ? `dao je ${current.aml_confirmed_by_name}` : 'još nitko nije dao'}.
                                </Note>
                                {isAdmin ? (
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        <Btn variant="soft" disabled={busy} onClick={() => act(current.id, 'confirm', { what: 'uprava' })}>Odobri (uprava)</Btn>
                                        <Btn variant="soft" disabled={busy} onClick={() => act(current.id, 'confirm', { what: 'spnft' })}>Potvrdi (SPNFT)</Btn>
                                        {['probni', 'aktivan', 'suspendiran', 'raskinut'].filter((s) => s !== current.status).map((s) => (
                                            <Btn key={s} variant={s === 'raskinut' || s === 'suspendiran' ? 'danger' : 'ghost'} disabled={busy}
                                                onClick={() => act(current.id, 'status', { status: s })}>Status {STATUS_LABEL[s].toLowerCase()}</Btn>
                                        ))}
                                    </div>
                                ) : null}
                            </>
                        ) : null}
                    </Section>
                </Card>
            ) : null}
        </>
    );
}
